import { cn } from '@/lib/utils';

import type { ActiveCardField } from './pan.utils';

export function PanCardMock({
  panNumber,
  name,
  fatherName,
  dob,
  activeField,
}: {
  panNumber?: string;
  name?: string;
  fatherName?: string;
  dob?: string;
  activeField?: ActiveCardField;
}) {
  const hasPan = Boolean(panNumber);
  const hasName = Boolean(name);
  const hasDob = Boolean(dob);

  // Narrowed off the value itself rather than through the has* booleans, which the
  // compiler can't follow — that indirection was the only reason these needed
  // non-null assertions. The booleans still drive the dimmed placeholder styling.
  const displayPan = panNumber ? panNumber.toUpperCase() : 'ABCDE1234F';
  const displayName = name || 'Smith Jhon';
  const displayDob = dob || '10/05/1980';

  const fieldHighlight =
    'rounded bg-white/40 shadow-[0_0_0_1.5px_#3B82F6] transition-all duration-150';

  return (
    // p-4 / mb-3 / mt-4 rather than p-5 / mb-4 / mt-5: ~16px off the card's height
    // with no change to the layout inside it. Together with the removed hint line
    // below the form, this is what gets the screen under a small phone's viewport.
    // `@container` (Tailwind v4) makes this card a query container, so the PAN number
    // below can size itself against the CARD's real width instead of the viewport's.
    // That is the only measurement that is correct in both places this card renders:
    // a real phone, and the admin's fixed-width phone frame on a desktop viewport.
    <div
      className="@container relative flex w-full flex-col rounded-xl p-4"
      style={{ background: 'var(--pan-card-gradient)' }}
    >
      <div className="mb-3 flex justify-between text-[11px] font-bold text-[#374151]">
        <span>INCOME TAX DEPARTMENT</span>
        <span>GOVT. OF INDIA</span>
      </div>
      {/* `min-w-0` on the row is what keeps the 76px QR inside the card: the PAN number
          below is `whitespace-nowrap`, and a nowrap child of a flex item will not shrink
          below its content width unless min-width is released — so on a narrow phone the
          middle column pushed the QR out through the card's right edge. Same fix as
          IdCardMock in the builder preview; this is the LIVE card, same structure.

          The middle column's budget is tight and FIXED: the row spends 32px padding +
          72px photo + 76px QR + two 16px gaps = 212px before the text gets any. On a
          390pt phone that leaves it about 99pt, which is why the PAN number's size is
          measured rather than chosen (see below). */}
      <div className="relative z-10 flex min-w-0 flex-1 items-start gap-4">
        <div className="h-full w-[72px] shrink-0 overflow-hidden rounded-md border border-white bg-[#94A3B8]">
          <img src="/pan-card-photo.png" alt="" className="h-full w-full object-cover" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          {/* The PAN must never be clipped — an applicant checking the card against what
              they typed has to see all 10 characters. A fixed px size cannot guarantee that,
              because the middle column's share of the row shrinks with the card: the row
              spends 32px padding + 72px photo + 76px QR + two 16px gaps = 212px before this
              text gets anything, and on a narrow phone the leftover was less than a 15px
              extrabold PAN needed, so `overflow-hidden` ate the last character (the "E" of
              FCEPB2042E just vanished).

              So the size is measured, not chosen: `cqw` is a percentage of the CARD's width,
              so the text shrinks exactly as fast as the space it has to fit in. 3.4cqw keeps
              ~10 characters plus tracking inside the leftover at every width; the 11px floor
              keeps it legible and the 15px ceiling stops it outgrowing the headline role on a
              wide desktop card. Tracking is clamped on the same axis so it never adds width
              the font size just gave back.

              Deliberately `cqw` and NOT `vw`: this card also renders inside the admin's
              fixed-width phone frame, where the viewport is the DESKTOP and a vw size would
              blow the text far past the frame. */}
          <div
            className={cn(
              'mb-2 inline-block whitespace-nowrap px-1 py-0.5 -mx-1 font-extrabold leading-none transition-all duration-150',
              'text-[clamp(11px,3.4cqw,15px)] tracking-[clamp(0.5px,0.3cqw,1.5px)]',
              hasPan ? 'text-[#1E3A8A]' : 'text-[#1E3A8A]/40',
              activeField === 'pan' && fieldHighlight,
            )}
          >
            {displayPan}
          </div>

          <div
            className={cn(
              'mb-1.5 flex flex-row items-start gap-1 px-1 py-0.5 -mx-1 rounded transition-all duration-150',
              activeField === 'name' && fieldHighlight,
            )}
          >
            <span className="shrink-0 text-[10px] font-medium leading-tight text-[#4B5563]">
              Name:
            </span>
            <span
              className={cn(
                'truncate text-[10px] font-bold leading-tight',
                hasName ? 'text-[#111827]' : 'text-[#111827]/40',
              )}
            >
              {displayName}
            </span>
          </div>

          {fatherName && (
            <div className="mb-1.5 flex flex-row items-start gap-1">
              <span className="shrink-0 text-[10px] font-medium leading-tight text-[#4B5563]">
                Father's Name:
              </span>
              <span className="truncate text-[10px] font-bold leading-tight text-[#111827]">
                {fatherName}
              </span>
            </div>
          )}

          <div
            className={cn(
              'mb-1.5 flex flex-row items-start gap-1 px-1 py-0.5 -mx-1 rounded transition-all duration-150',
              activeField === 'dob' && fieldHighlight,
            )}
          >
            <span className="shrink-0 text-[10px] font-medium leading-tight text-[#4B5563]">
              Date Of Birth:
            </span>
            <span
              className={cn(
                'truncate text-[10px] font-bold leading-tight',
                hasDob ? 'text-[#111827]' : 'text-[#111827]/40',
              )}
            >
              {displayDob}
            </span>
          </div>
        </div>
        <div className="h-[76px] w-[76px] shrink-0 rounded-md bg-white p-1.5">
          <img src="/pan-card-qr.png" alt="" className="h-full w-full object-cover" />
        </div>
      </div>
      <div className="relative z-10 mt-4">
        <div className="flex flex-row items-center gap-2">
          <span className="text-[10px] font-medium text-[#111827]">Card Holder's Signature</span>
          <div className="mb-0.5 h-3 w-[48px] border-b border-[#111827]" />
        </div>
      </div>
    </div>
  );
}
